/**
 * Answering an invitation (Outlook "Respond"): Accept / Tentative / Decline send the answer at once; "More" opens the
 * response dialog to add a message or to answer without telling the organizer. Used by the event panel, the hover
 * preview and the meeting card's calendar side. Undo restores the previous answer (undo.ts).
 */
import { useI18n } from '@platform/i18n';
import { Button, Checkbox, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Label, SegmentedControl, Textarea } from '@platform/ui';
import { CheckIcon, HelpCircleIcon, MessageSquareIcon, XIcon } from 'lucide-react';
import { useId, useState } from 'react';
import type { CalendarEvent } from '../api';
import { useUndoableCalendar } from './undo';

type Answer = 'accept' | 'tentative' | 'decline';

export function RsvpButtons({ event, onDone, size = 'sm' }: { event: CalendarEvent; onDone?: () => void; size?: 'sm' | 'xs' }) {
  const { t } = useI18n();
  const { respond } = useUndoableCalendar();
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(false);
  const answer = async (response: Answer, options?: { comment?: string; send?: boolean }) => {
    setBusy(true);
    const ok = await respond(event, response, options);
    setBusy(false);
    if (ok) onDone?.();
    return ok;
  };
  const small = size === 'xs' ? 'h-7 px-2 text-xs' : undefined;
  return (
    <>
      <Button size="sm" className={small} disabled={busy} onClick={() => void answer('accept')}>
        <CheckIcon />
        {t('webmail.calendar.accept')}
      </Button>
      <Button size="sm" variant="outline" className={small} disabled={busy} onClick={() => void answer('tentative')}>
        <HelpCircleIcon />
        {t('webmail.calendar.tentative')}
      </Button>
      <Button size="sm" variant="outline" className={small} disabled={busy} onClick={() => void answer('decline')}>
        <XIcon />
        {t('webmail.calendar.decline')}
      </Button>
      <Button size="icon-sm" variant="ghost" disabled={busy} aria-label={t('webmail.calendarUx.rsvp.more')} title={t('webmail.calendarUx.rsvp.more')} onClick={() => setDialog(true)}>
        <MessageSquareIcon />
      </Button>
      {dialog && <RsvpDialog initial={event.myResponse === 'declined' ? 'decline' : event.myResponse === 'tentative' ? 'tentative' : 'accept'} onClose={() => setDialog(false)} onSubmit={answer} />}
    </>
  );
}

function RsvpDialog({ initial, onClose, onSubmit }: { initial: Answer; onClose: () => void; onSubmit: (response: Answer, options: { comment?: string; send?: boolean }) => Promise<boolean> }) {
  const { t } = useI18n();
  const messageId = useId();
  const [response, setResponse] = useState<Answer>(initial);
  const [comment, setComment] = useState('');
  const [send, setSend] = useState(true);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('webmail.calendarUx.rsvp.title')}</DialogTitle>
          <DialogDescription>{t('webmail.calendarUx.rsvp.description')}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            void onSubmit(response, { send, ...(send && comment.trim() && { comment: comment.trim() }) }).then((ok) => {
              setBusy(false);
              if (ok) onClose();
            });
          }}
        >
          <SegmentedControl<Answer>
            label={t('webmail.calendarUx.rsvp.answer')}
            value={response}
            options={[
              { value: 'accept', label: t('webmail.calendar.accept') },
              { value: 'tentative', label: t('webmail.calendar.tentative') },
              { value: 'decline', label: t('webmail.calendar.decline') },
            ]}
            onChange={setResponse}
          />
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={send} onCheckedChange={(v) => setSend(v === true)} />
            {t('webmail.calendarUx.rsvp.send')}
          </label>
          {send && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={messageId}>{t('webmail.calendarUx.rsvp.message')}</Label>
              <Textarea id={messageId} rows={4} value={comment} maxLength={2000} onChange={(e) => setComment(e.target.value)} />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={busy}>
              {t(send ? 'webmail.calendarUx.rsvp.submitSend' : 'webmail.calendarUx.rsvp.submitSave')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
